import { CSSProperties } from 'react'

import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export const containerStyles = (
  colorScheme: ColorScheme = 'dark',
  disabled = false,
  isReadOnly = false
): SystemStyleObject => {
  const isDark = colorScheme === 'dark'
  const isMuted = disabled || isReadOnly
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative',
    pl: '8px',
    pr: '52px', // reserve space for absolutely-positioned controls
    py: '6px',
    borderRadius: '0',
    bg: isDark ? 'bg.input' : 'light.bg.input',
    // Read-only fields retain the disabled visual treatment while remaining
    // openable so truncated selections can still be inspected.
    cursor: isMuted ? 'not-allowed' : 'pointer',
    opacity: isMuted ? 0.5 : 1,
    width: '100%',
    boxSizing: 'border-box',
    // Identical keyboard-focus treatment in both schemes; only the ring
    // color token differs.
    _focusVisible: {
      outline: '2px solid',
      outlineColor: isDark
        ? 'interactive.focus-ring'
        : 'light.interactive.focus-ring',
      outlineOffset: '2px',
    },
  }
}

// Selection field subtitle slot — Figma "Detail" font: Söhne Kräftig 10/16,
// 1px tracking, uppercase, text/secondary in both modes.
export const inputLabelStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontSize: '10px',
  fontWeight: '500',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  lineHeight: '16px',
  mb: '4px',
  color: colorScheme === 'dark' ? 'text.secondary' : 'light.text.secondary',
  userSelect: 'none',
})

export const inputRowStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  minHeight: '28px',
}

export const chipsAreaStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: '6px',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
}

export const overflowBadgeStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  height: '20px',
  minWidth: '20px',
  px: '4px',
  borderRadius: 'full',
  bg: colorScheme === 'dark' ? 'bg.badge-button' : 'light.bg.badge-button',
  color:
    colorScheme === 'dark' ? 'text.badge-button' : 'light.text.badge-button',
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontSize: '11px',
  lineHeight: '16px',
  userSelect: 'none',
})

// Trigger-field placeholder — Small/Body (11/16) at text/primary, matching
// the Select trigger so every dropdown field in a form reads identically
// (product decision: text/secondary is illegible on bg/input).
export const placeholderStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne', sans-serif",
  fontSize: '11px',
  lineHeight: '16px',
  color: colorScheme === 'dark' ? 'text.primary' : 'light.text.primary',
  userSelect: 'none',
})

export const controlsStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
}

export const chipWrapperStyle = (isHidden: boolean): CSSProperties => ({
  flexShrink: 0,
  display: isHidden ? 'none' : undefined,
})

export const overflowEllipsisStyle = (hiddenCount: number): CSSProperties =>
  hiddenCount > 0
    ? { flexShrink: 0 }
    : { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }

export const chevronStyle = (isOpen: boolean): CSSProperties => ({
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.15s ease',
})

export const controlButtonStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: colorScheme === 'dark' ? 'icon.secondary' : 'light.icon.secondary',
  borderRadius: '2px',
  lineHeight: 0,
  _hover: {
    opacity: 0.7,
  },
  _focusVisible: {
    outline: '2px solid',
    outlineColor:
      colorScheme === 'light'
        ? 'light.interactive.focus-ring'
        : 'interactive.focus-ring',
    outlineOffset: '1px',
  },
})
